/**
 * Data behind the analysis and neuroimage viewers (`app/services/visualization_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { assert } from '@brainfocus/web/utils/assert';
import { Npyjs } from '@brainfocus/contrib/npyjs';
import { Observable } from 'rxjs';
import { buildSignalQuery, SignalQuery } from '@brainfocus/web/sources/signal_query';
import { NormType } from '@brainfocus/web/analysis/models';
import { decodeVolumePayload } from '@brainfocus/web/neuroimage_decode_client';
import type { Brain3dInputs } from '@brainfocus/web/domain/neuroimage/brain_3d';
import { fetchEegData } from '@brainfocus/web/api/eeg_data';
import { assertPojo } from '@brainfocus/web/api/response';

export async function automaticDetection(
    id: number,
    thresholdGa: number,
    thresholdAe: number,
    zscoreThreshold?: number,
    isFocal?: boolean,
    minDurationEnforced?: boolean,
    preOnsetDetection?: boolean,
): Promise<AutomaticDetectionResponse> {
    const response = await client.request('POST', `/api/automatic-detection`, {
        recording_id: id,
        t_ga_tile: thresholdGa,
        t_ae: thresholdAe,
        zscore_threshold: zscoreThreshold,
        is_focal: isFocal,
        min_duration_enforced: minDurationEnforced,
        // #943 follow-up: sent live like the params above -- MA/GA/AE
        // always cover the whole recording now, so this is a pure
        // display filter with no recompute.
        pre_onset_detection: preOnsetDetection,
    });
    assertPojo<AutomaticDetectionResponse>(response);
    return response;
}

/**
 * Load raw EEG data for all given channels in form of 2D array with data
 * ordered according to the other of input channels array.
 */
export function getEeg(
    recId: number,
    channels: number[],
    query?: SignalQuery,
): Observable<{ progress: number } | Float32Array[]> {
    return fetchEegData(`/api/get-eeg/${recId}/${channels.join(',')}${buildSignalQuery(query)}`);
}

/**
 * Load TC plot for all contacts in a finished analysis
 * @param freqRange Indexes of the floor and ceiling frequency bands
 * @param decimated Optional target points per channel (mean per bin, #663)
 * @param range Optional `[from, to)` sample range; with `decimated` this is a
 *   bounded viewport slice of the visible range (#983)
 */
export async function getTcPlot(
    recId: number,
    freqRange: [number, number],
    decimated?: number,
    range?: { from: number; to: number },
): Promise<Float32Array[]> {
    const freqRangeParam = `${freqRange[0]}-${freqRange[1] - 1}`;
    const query = decimated != null || range ? { decimated, ...range } : undefined;
    const url = `/api/get-tcplot/${recId}/${freqRangeParam}${buildSignalQuery(query)}`;
    const r = await client.request('GET', url);
    assert(r instanceof ArrayBuffer, 'Get TC endpoint returned unexpected data');
    const npyjs = new Npyjs();
    const npy = npyjs.parse(r);
    const rows = npy.shape[0] ?? 0;
    const columns = npy.shape[1] ?? 0;
    const data = new Array(rows).fill(null)
        .map((_, i) => npy.data.subarray(i * columns, (i + 1) * columns));
    return data;
}

/**
 * Load the stationarity-driven baseline TC block, summed over a band range,
 * for all contacts (#582). Mirrors {@link getTcPlot}; the returned per-channel
 * arrays span the baseline window and are reduced to obtain normalization
 * statistics over that window.
 * @param freqRange Indexes of the floor and ceiling frequency bands
 */
export async function getBaseline(recId: number, freqRange: [number, number]): Promise<Float32Array[]> {
    const freqRangeParam = `${freqRange[0]}-${freqRange[1] - 1}`;
    const url = `/api/get-baseline/${recId}/${freqRangeParam}`;
    const r = await client.request('GET', url);
    assert(r instanceof ArrayBuffer, 'Get baseline endpoint returned unexpected data');
    const npyjs = new Npyjs();
    const npy = npyjs.parse(r);
    const rows = npy.shape[0] ?? 0;
    const columns = npy.shape[1] ?? 0;
    const data = new Array(rows).fill(null)
        .map((_, i) => npy.data.subarray(i * columns, (i + 1) * columns));
    return data;
}

/**
 * Load all required data for the Brain3D visualization from the API
 */
export async function getBrain3dInputs(analysisId: number, mriRecordId?: number | null): Promise<Brain3dInputs | null> {
    // The path only carries the analysis id, but the payload is whichever MRI is
    // assigned to it — so after a reassignment the URL is byte-identical while the
    // bytes behind it have changed, and the browser HTTP cache happily serves the
    // previous scan's zip. #682 hit exactly this on get-mri-3d (see below).
    //
    // `mri` makes the URL scan-specific and `t` defeats the cache outright. Both are
    // ignored server-side — get_brain_3d takes only the path param — so no backend
    // change is needed (#822).
    const scan = mriRecordId != null ? `mri=${mriRecordId}&` : '';
    return decodeVolumeResponse(
        () => client.request('GET', `/api/get-brain-3d/${analysisId}?${scan}t=${Date.now()}`),
    );
}

/**
 * Load all required data for MRI 3D visualization by MRI ID.
 * Used when viewing MRI from patient workspace without an analysis.
 */
export async function getMri3dInputs(mriId: number): Promise<Brain3dInputs | null> {
    // Cache-bust: this payload changes when a segmentation is uploaded/removed,
    // and the response is otherwise served from the browser HTTP cache — so a
    // refetch after a seg change would return the stale zip. A per-call token
    // forces a fresh network response (#682). The loader's in-memory promise
    // cache still dedupes repeated views of the same MRI.
    //
    // HOTFIX NOTE (#682): this always cache-busts get-mri-3d, disabling HTTP
    // caching for it entirely. A more surgical approach (server-side no-cache /
    // ETag headers, or busting only after an invalidation) should be analyzed
    // in a follow-up issue rather than in this hotfix.
    return decodeVolumeResponse(
        () => client.request('GET', `/api/get-mri-3d/${mriId}?t=${Date.now()}`),
    );
}

/**
 * Fetch the stored #690 tilt-correction matrix for an MRI record (`no-store`). Not used by
 * the viewers, which derive "Correct tilt" from the affine; kept for a possible future
 * "Align to AC-PC" option.
 */
export async function getMriTiltCorrection(mriId: number): Promise<number[][] | null> {
    const r = await client.request('GET', `/api/mri/${mriId}/tilt-correction`);
    if (!r || typeof r !== 'object' || !('tiltCorrection' in r)) return null;
    return (r as { tiltCorrection: number[][] | null }).tiltCorrection ?? null;
}

/**
 * Load the volume payload for an uploaded NeuroimageFile by its ID (#620).
 *
 * Parallel to `getMri3dInputs`, but sourced from a `NeuroimageFile` via
 * `/api/neuroimage/<id>/3d` — lets a clinician view an uploaded/converted
 * scan (native NIfTI, or DICOM once converted) before coregistration,
 * without an `MRIRecord`. Same `Brain3dInputs` shape the 2D preview consumes.
 */
export async function getNeuroimage3dInputs(fileId: number): Promise<Brain3dInputs | null> {
    // Cache-bust for parity with getMri3dInputs (the browser HTTP cache would
    // otherwise serve a stale zip after a re-view); the loader's in-memory
    // promise cache still dedupes repeated views of the same file.
    return decodeVolumeResponse(
        () => client.request('GET', `/api/neuroimage/${fileId}/3d?t=${Date.now()}`),
    );
}

/**
 * Load GA/AE metrices for given recording
 */
export async function getGaae(recId: number): Promise<GaaeResult> {
    const url = `/api/get-gaae/${recId}`;
    const r = await client.request('GET', url);
    return parseGaaeResponse(r);
}

/**
 * Load GA/AE metrices for given recording
 */
export async function calculateGaae(recId: number, channels: number[]): Promise<GaaeResult> {
    const url = `/api/calculate-gaae`;
    const r = await client.request('POST', url, {
        recording_id: recId,
        channels,
    });
    return parseGaaeResponse(r);
}

export function parseGaaeResponse(r: any): GaaeResult {
    assert(r instanceof ArrayBuffer, 'Get GAAE endpoint returned unexpected data');
    const npyjs = new Npyjs();
    const npy = npyjs.parse(r);
    const rows = npy.shape[1] ?? 0;
    const columns = npy.shape[2] ?? 0;
    const matrixSize = rows * columns;
    const ga = npy.data.subarray(0, matrixSize);
    const ae = npy.data.subarray(matrixSize, 2 * matrixSize);
    const out = [ga, ae].map(mtx => new Array(rows).fill(null)
        .map((_, i) => mtx.subarray(i * columns, (i + 1) * columns)));
    return {
        ga: out[0]!,
        ae: out[1]!,
    };
}

/**
 * Fetch a volume payload and decode it off the main thread (#670).
 *
 * Shared by the three volume endpoints, which differ only in their URL and
 * cache-busting. Inflating and parsing the payload inline used to freeze the
 * analysis page for 20+ s, so the buffer is handed to the decode worker and NOT
 * touched afterwards — it is transferred, hence detached on this side.
 *
 * @param fetchPayload Performs the request; returns `{ data: null }` when there
 *     is no neuroimage to load.
 */
async function decodeVolumeResponse(
    fetchPayload: () => Promise<object | ArrayBuffer | string>,
): Promise<Brain3dInputs | null> {
    const fetchStartedAt = performance.now();
    let response: object | ArrayBuffer | string;
    try {
        response = await fetchPayload();
    } catch (e) {
        // request() throws an ApiError for any HTTP error with a JSON `error`. A 4xx is a
        // refusal with a reason meant for the user (#1045); anything else stays as it was.
        if (e instanceof client.ApiError && e.code >= 400 && e.code < 500) {
            throw new VolumeLoadError(e.apiError);
        }
        throw e;
    }
    if (typeof response === 'object' && 'data' in response && response.data === null) {
        return null;
    }
    assert(response instanceof ArrayBuffer, 'Invalid MRI data format');
    const fetchMs = performance.now() - fetchStartedAt;

    const { volume, decodeMs, offThread } = await decodeVolumePayload(response);
    return {
        metadata: volume.metadata,
        mri: volume.mri,
        stlData: volume.stlData,
        segLabels: volume.segLabels,
        // Explicit: a field dropped here never reaches the viewers (#989; the CT texture was).
        overlays: volume.overlays,
        loadTimings: { fetchMs, decodeMs, offThread },
    };
}

/**
 * A volume request the server refused with a reason meant for the user, e.g. "The neuroimage
 * file is too large once decompressed" or "Neuroimage is still being converted" (#1045). The
 * viewers show its message; any other loading failure keeps their generic text.
 */
export class VolumeLoadError extends Error {

    public override name = 'VolumeLoadError';

}

export interface GaaeResult {
    ga: Float32Array[];
    ae: Float32Array[];
}

export interface AutomaticDetectionResponse {
    /**
     * List of detections
     */
    result: SozRow[];
    /**
     * Whether a SAM baseline window was available for this recording (#733).
     * When `false` and `result` is empty, the pipeline could not produce a SOZ
     * prediction and the UI shows a no-baseline empty state.
     */
    baselineAvailable: boolean;
    /**
     * Normalization mode that produced this result (#733).
     */
    normType: NormType;
}

export interface SozRow {
    /**
     * Frequency band index (0 = freq[0] to freq[1])
     */
    freqIndex: number;
    /**
     * Time interval in seconds relative to the analysis onset (negative before it). It equals the
     * seizure onset until the analysis times are modified.
     */
    interval: [number, number];
    /**
     * List of channels in which the activity was detected
     */
    channels: number[];
}
